import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {useHistory} from 'react-router-dom'
import styled from '@emotion/styled'
import CloseIcon from '@material-ui/icons/Close'
import {IconButton} from '@material-ui/core'
import messages from '../../../../assets/messages'
import {getFormattedAmount} from '../../../../common/utils'
import LoadingButton from '../../../Common/LoadingButton'
import {PendingPammOffer, usePendingPammOffers} from './hooks/usePendingPammOffers'

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0 1.5rem;
`

const HeaderRow = styled.div`
  align-items: baseline;
  display: flex;
  font-family: Roboto;
  justify-content: space-between;
`

const Title = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
`

const Counter = styled.span`
  color: #73738C;
  font-size: 0.875rem;
`

const Card = styled.div`
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #E5E7EE;
  border-radius: 0.5rem;
  display: flex;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
`

const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
`

const OfferTitle = styled.div`
  color: #1C212E;
  font-family: Roboto;
  font-size: 1rem;
  font-weight: 600;
`

const OfferSummary = styled.div`
  color: #73738C;
  font-family: Roboto;
  font-size: 0.875rem;
`

type PendingOfferCardProps = {
  offer: PendingPammOffer
  onDismiss: () => void
  onReview: () => void
} & WithNamespaces

const _PendingOfferCard = ({offer, onDismiss, onReview, t}: PendingOfferCardProps) => (
  <Card>
    <CardBody>
      <OfferTitle>{offer.name}</OfferTitle>
      <OfferSummary>
        <Trans
          {...messages.pendingPammOfferSummary}
          values={{
            managerName: offer.managerName,
            minInitialInvestment: getFormattedAmount({
              amount: offer.minInitialInvestment,
              currency: offer.currency,
            }),
            performanceFee: Math.round(offer.performanceFeeTopLevel),
          }}
        />
      </OfferSummary>
    </CardBody>
    <LoadingButton
      disabled={false}
      id={`reviewPendingPammOffer-${offer.managerId}-${offer.offerId}`}
      onClick={onReview}
    >
      <Trans {...messages.pendingPammOfferReview} />
    </LoadingButton>
    <IconButton
      aria-label={t(messages.pendingPammOfferDismiss.i18nKey, messages.pendingPammOfferDismiss.defaults)}
      onClick={onDismiss}
      size='small'
    >
      <CloseIcon />
    </IconButton>
  </Card>
)

const PendingOfferCard = withNamespaces()(_PendingOfferCard)

/**
 * Floating block rendered above the accounts list (PT-5354). Lists offers
 * the investor has visited but not accepted, with "Review offer" linking
 * back to the offer page and an "x" to dismiss the reminder. Renders nothing
 * while loading or when the list is empty.
 */
export const PendingPammOffers = () => {
  const history = useHistory()
  const {dismiss, loading, pendingOffers} = usePendingPammOffers()

  if (loading || pendingOffers.length === 0) return null

  return (
    <Container>
      <HeaderRow>
        <Title>
          <Trans {...messages.pendingPammOffersTitle} />
        </Title>
        <Counter>
          <Trans {...messages.pendingPammOffersAwaitingAction} values={{count: pendingOffers.length}} />
        </Counter>
      </HeaderRow>
      {pendingOffers.map((offer) => (
        <PendingOfferCard
          key={`${offer.managerId}_${offer.offerId}`}
          offer={offer}
          onDismiss={() => dismiss({managerId: offer.managerId, offerId: offer.offerId})}
          onReview={() => history.push(`/pamm-offer/${offer.managerId}/${offer.offerId}`)}
        />
      ))}
    </Container>
  )
}
